import { forwardRef, type ComponentType, type SVGProps } from 'react';

export type IconProps = Omit<SVGProps<SVGSVGElement>, 'name'> & {
  size?: number | string;
  label?: string;
  title?: string;
};

type SvgComponent = ComponentType<SVGProps<SVGSVGElement> & { title?: string; titleId?: string }>;

/** Shared presentation only: importing one named icon never imports the registry. */
export function createIcon(Component: SvgComponent, name: string, defaults: Pick<SVGProps<SVGSVGElement>, 'fill'> = {}) {
  const Icon = forwardRef<SVGSVGElement, IconProps>(function LocalIcon(
    { size = '1em', label, title, ...props },
    ref,
  ) {
    const accessibleLabel = label ?? props['aria-label'];
    const named = Boolean(accessibleLabel || props['aria-labelledby'] || title);

    return (
      <Component
        {...defaults}
        width={size}
        height={size}
        focusable="false"
        aria-hidden={named ? undefined : true}
        aria-label={accessibleLabel}
        role={named ? 'img' : undefined}
        title={title ?? label}
        data-unitfield-icon={name}
        {...props}
        ref={ref}
      />
    );
  });

  Icon.displayName = `UnitfieldIcon(${name})`;
  return Icon;
}
